import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Container,
  TextField,
  MenuItem,
  Button,
  Link
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BurgerBar from '../Materials/BurgerBar';
import Footer from '../Materials/Footer';
import './Orientedsearch.css';

export default function MainPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchCategory, setSearchCategory] = React.useState('');
  const [searchResults, setSearchResults] = React.useState(null);
  
  // Estados originais da API
  const [processTypes, setProcessTypes] = React.useState([]);
  const [matrices, setMatrices] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [entities, setEntities] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [equipments, setEquipments] = React.useState([]);

  // Buscar dados originais da API
  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Chamadas originais à API
        const processTypesResponse = await fetch('https://api.hub4food.colab4food.com/api/process-types');
        if (!processTypesResponse.ok) throw new Error('Erro ao buscar tipos de processo');
        const processTypesData = await processTypesResponse.json();
        setProcessTypes(processTypesData);

        const matricesResponse = await fetch('https://api.hub4food.colab4food.com/api/matrices');
        if (!matricesResponse.ok) throw new Error('Erro ao buscar matrizes');
        const matricesData = await matricesResponse.json();
        setMatrices(matricesData);

        const projectsResponse = await fetch('https://api.hub4food.colab4food.com/api/projects');
        if (!projectsResponse.ok) throw new Error('Erro ao buscar projetos');
        const projectsData = await projectsResponse.json();
        setProjects(projectsData);

        const equipmentsResponse = await fetch('https://api.hub4food.colab4food.com/api/equipments');
if (!equipmentsResponse.ok) throw new Error('Erro ao buscar equipamentos');
const equipmentsData = await equipmentsResponse.json();
setEquipments(equipmentsData);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCategoryChange = (e) => {
    setSearchCategory(e.target.value);
    setSearchResults(null);
  };

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    if (!searchCategory) {
      setError('Please select a category');
      return;
    }

    let results = [];
    const term = searchTerm.toLowerCase();

    // Mapeando as categorias solicitadas para os dados existentes
    switch (searchCategory) {
      case 'projects':
        results = projects
          .filter(project => 
            project.project_name.toLowerCase().includes(term) ||
            (project.description && project.description.toLowerCase().includes(term))
          )
          .map(project => ({
            id: project.id,
            name: project.project_name,
            type: 'Project',
            url: `/project/${project.id}`
          }));
        break;
        
      case 'equipments':
  results = equipments
    .filter(equipment => 
      equipment.equipment_name.toLowerCase().includes(term) ||  
      (equipment.description && equipment.description.toLowerCase().includes(term))
    )
    .map(equipment => ({
      id: equipment.id,
      name: equipment.equipment_name,  
      type: 'Equipment',
      url: `/detailedEquipment/${equipment.id}`  
    }));
  break;
        
      case 'research-lines':
        // Usando 'process-types' como linhas de investigação (ajuste conforme necessário)
        results = processTypes
          .filter(processType => 
            processType.description.toLowerCase().includes(term)
          )
          .map(processType => ({
            id: processType.id,
            name: processType.description,
            type: 'Research Line',
            url: `/process-type/${processType.id}`
          }));
        break;
        
      default:
        break;
    }

    setSearchResults(results.length > 0 ? results : null);
    setError(null);
  };

  const handleClearFilters = () => {
    setSearchCategory('');
    setSearchTerm('');
    setSearchResults(null);
    setError(null);
  };

  const isSearchEnabled = searchCategory && searchTerm.trim().length > 0;

  return (
    <Box className="main-container">
      <BurgerBar />
      
      <Box className="main-content">
        <img src="logotipo.png" alt="logo" className="main-logo" />

        <Container maxWidth="lg">
          <Box className="oriented-search-header">
            <Typography variant="h6" color="white">
              Oriented Search
            </Typography>
          </Box>
          <Box className="oriented-search-body">
            {loading && <Typography>Loading data...</Typography>}
            {error && <Typography color="error">{error}</Typography>}
            
           <Grid container spacing={2} alignItems="center">
  <Grid item xs={12} sm={4}>
    <TextField
      select
      label="Category"
      fullWidth
      value={searchCategory}
      onChange={handleCategoryChange}
      SelectProps={{
        IconComponent: ExpandMoreIcon,
      }}
    >
      <MenuItem value="">
        <em>Select a category</em>
      </MenuItem>
      <MenuItem value="projects">Projects</MenuItem>
      <MenuItem value="equipments">Equipments</MenuItem>
      <MenuItem value="research-lines">Research Lines</MenuItem>
    </TextField>
  </Grid>

  <Grid item xs={12} sm={4}>
    <TextField
      label="Search term"
      fullWidth
      value={searchTerm}
      onChange={handleSearchTermChange}
      placeholder="Enter search term..."
    />
  </Grid>

  <Grid item xs={6} sm={2}>
    <Button
      variant="contained"
      color="primary"
      onClick={handleSearch}
      disabled={!isSearchEnabled}
      fullWidth
    >
      Search
    </Button>
  </Grid>

  <Grid item xs={6} sm={2}>
    <Button
      variant="outlined"
      onClick={handleClearFilters}
      disabled={!searchCategory && !searchTerm}
      fullWidth
    >
      Clear
    </Button>
  </Grid>
</Grid>


            <Box className="results-container" sx={{ mt: 3 }}>
              {searchResults ? (
                <Box sx={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '4px',
                  p: 2
                }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Search Results:
                  </Typography>
                  <Box component="ul" sx={{ 
                    listStyleType: 'none', 
                    p: 0,
                    '& li': { py: 1, borderBottom: '1px solid #eee' }
                  }}>
                    {searchResults.map((result, index) => (
                      <li key={index}>
                        <Link
                          href={result.url}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(result.url);
                          }}
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          <Box sx={{ 
                            bgcolor: 'primary.light', 
                            color: 'primary.contrastText',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            mr: 1,
                            fontSize: 12
                          }}>
                            {result.type}
                          </Box>
                          {result.name}
                        </Link>
                      </li>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ 
                  textAlign: 'center',
                  py: 3
                }}>
                  No results to display. Please select a category, enter a search term and click Search.
                </Typography>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}